import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import MonthField from '../components/common/MonthField';
import { RankChip } from '../components/projectReport/IssueList';
import apiService from '../services/api.service';
import { IssueRow, IssueSection, RANKS, SECTION_LABELS } from '../types/projectReport';
import { currentMonth, monthLabel } from '../utils/projectReport';

/**
 * Projects > Project Issues (management): every ranked safety issue and concern of the submitted
 * monthly reports, across the projects and companies the user may see; filter by rank, type, status,
 * company, project and month; open the report; export to Excel.
 */

type CompanyOption = { companyId: number; companyName: string };

const ProjectIssues: React.FC = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState<string>('all');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [ranks, setRanks] = useState<number[]>([1, 2, 3, 4]);
  const [section, setSection] = useState<'' | IssueSection>('');
  const [status, setStatus] = useState<'Open' | 'Resolved' | ''>('Open');
  const [latestOnly, setLatestOnly] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [project, setProject] = useState('');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState({ project: '', q: '' });
  const [rows, setRows] = useState<IssueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced({ project: project.trim(), q: q.trim() }), 350);
    return () => clearTimeout(timer);
  }, [project, q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.getProjectIssues({
      companyId: company === 'all' ? undefined : company,
      sections: section || undefined,
      status: status || undefined,
      latestOnly: latestOnly ? 'true' : 'false',
      from: !latestOnly && from ? from : undefined,
      to: !latestOnly && to ? to : undefined,
      project: debounced.project || undefined,
      q: debounced.q || undefined,
    })
      .then((res: any) => {
        if (cancelled) return;
        setRows(Array.isArray(res?.data?.rows) ? res.data.rows : []);
        if (Array.isArray(res?.data?.companies)) setCompanies(res.data.companies);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.message || 'The issues could not be loaded'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [company, section, status, latestOnly, from, to, debounced]);

  const counts = useMemo(() => {
    const c: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
    for (const r of rows) c[r.rank] = (c[r.rank] || 0) + 1;
    return c;
  }, [rows]);
  const visible = useMemo(() => rows.filter((r) => ranks.includes(r.rank)), [rows, ranks]);
  const manyCompanies = new Set(rows.map((r) => r.companyId)).size > 1 || company === 'all';

  const toggleRank = (rank: number) =>
    setRanks((prev) => (prev.includes(rank) ? prev.filter((r) => r !== rank) : [...prev, rank].sort()));

  const exportExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const book = XLSX.utils.book_new();
      const sheet = XLSX.utils.json_to_sheet(visible.map((r) => ({
        Rank: RANKS.find((x) => x.rank === r.rank)?.label || r.rank,
        Type: SECTION_LABELS[r.section],
        Issue: r.description,
        'Action / support needed': r.action,
        Status: r.status,
        Company: r.companyName,
        Project: r.projectCode,
        'Project name': r.projectName,
        'Report month': monthLabel(r.month),
        'Report No.': r.reportNo,
        'Reported since': monthLabel(r.firstPeriodMonth),
        'Project manager': r.preparedBy,
      })));
      sheet['!cols'] = [{ wch: 10 }, { wch: 16 }, { wch: 60 }, { wch: 40 }, { wch: 10 }, { wch: 30 }, { wch: 16 }, { wch: 40 }, { wch: 16 }, { wch: 10 }, { wch: 16 }, { wch: 24 }];
      if (sheet['!ref']) sheet['!autofilter'] = { ref: sheet['!ref'] };
      XLSX.utils.book_append_sheet(book, sheet, 'Project issues');
      XLSX.writeFile(book, `Project-Issues-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      setError('The Excel export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const columns: ResponsiveTableColumn<IssueRow>[] = [
    { id: 'rank', header: 'Rank', cell: (r) => <RankChip rank={r.rank} />, width: 100 },
    {
      id: 'issue',
      header: 'Issue',
      cell: (r) => (
        <Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{r.description}</Typography>
          {r.action && <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>Action: {r.action}</Typography>}
        </Box>
      ),
    },
    { id: 'type', header: 'Type', cell: (r) => SECTION_LABELS[r.section], hideOnMobile: true },
    {
      id: 'project',
      header: 'Project',
      cell: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{r.projectCode}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{r.projectName}</Typography>
          {manyCompanies && <Typography variant="caption" color="text.secondary">{r.companyName}</Typography>}
        </Box>
      ),
    },
    {
      id: 'month',
      header: 'Report',
      cell: (r) => (
        <Box>
          <Typography variant="body2">{monthLabel(r.month)}</Typography>
          {r.firstPeriodMonth && r.firstPeriodMonth.slice(0, 7) < r.month.slice(0, 7) && (
            <Typography variant="caption" color="text.secondary">since {monthLabel(r.firstPeriodMonth)}</Typography>
          )}
        </Box>
      ),
      hideOnMobile: true,
    },
    { id: 'pm', header: 'Project manager', cell: (r) => r.preparedBy, hideOnMobile: true },
    { id: 'status', header: 'Status', cell: (r) => <Chip size="small" label={r.status} color={r.status === 'Open' ? 'default' : 'success'} variant="outlined" /> },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="h5" component="h1">Project Issues</Typography>
          <Typography variant="body2" color="text.secondary">The ranked safety issues and concerns of the submitted monthly project reports.</Typography>
        </Box>
        <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />} disabled={exporting || !visible.length} onClick={() => void exportExcel()}>
          Export to Excel
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gap: 1.5 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" role="group" aria-label="Ranks">
          {RANKS.map((r) => {
            const on = ranks.includes(r.rank);
            return (
              <Chip
                key={r.rank}
                label={`${r.label} (${counts[r.rank] || 0})`}
                onClick={() => toggleRank(r.rank)}
                aria-pressed={on}
                variant={on ? 'filled' : 'outlined'}
                sx={on ? { bgcolor: r.color, color: r.text, fontWeight: 600, '&:hover': { bgcolor: r.color, opacity: 0.9 } } : { borderColor: r.color, color: r.color }}
              />
            );
          })}
        </Stack>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(5, 1fr)' } }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="issues-company">Company</InputLabel>
            <Select labelId="issues-company" label="Company" value={company} onChange={(e) => setCompany(String(e.target.value))}>
              <MenuItem value="all">All my companies</MenuItem>
              {companies.map((c) => <MenuItem key={c.companyId} value={String(c.companyId)}>{c.companyName}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="issues-type">Type</InputLabel>
            <Select labelId="issues-type" label="Type" value={section} onChange={(e) => setSection(e.target.value as '' | IssueSection)}>
              <MenuItem value="">All types</MenuItem>
              {(Object.keys(SECTION_LABELS) as IssueSection[]).map((s) => <MenuItem key={s} value={s}>{SECTION_LABELS[s]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="issues-status">Status</InputLabel>
            <Select labelId="issues-status" label="Status" value={status} onChange={(e) => setStatus(e.target.value as 'Open' | 'Resolved' | '')}>
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Resolved">Resolved</MenuItem>
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Project" value={project} onChange={(e) => setProject(e.target.value)} placeholder="Code or name" />
          <TextField
            size="small"
            label="Search issues"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <FormControlLabel control={<Switch checked={latestOnly} onChange={(e) => setLatestOnly(e.target.checked)} />} label="Latest report of each project" />
          {!latestOnly && (
            <>
              <MonthField label="From" value={from} onChange={setFrom} max={to || currentMonth()} clearable />
              <MonthField label="To" value={to} onChange={setTo} min={from || undefined} max={currentMonth()} clearable />
            </>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}
      {loading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" role="status"><CircularProgress size={20} /><Typography>Loading issues…</Typography></Stack>
      ) : (
        <ResponsiveTable
          ariaLabel="Project issues"
          columns={columns}
          rows={visible}
          getRowKey={(r) => r.issueId}
          onRowClick={(r) => navigate(`/project-reports/${r.reportId}`)}
          emptyMessage={rows.length ? 'No issue with the chosen ranks.' : 'No issue reported in submitted reports for these filters.'}
        />
      )}
    </Box>
  );
};

export default ProjectIssues;
